import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { Smile } from "lucide-react";
import { useEditorRef, useEditorSelector } from "platejs/react";
import { memo, useCallback, useRef, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "../ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useToolbarOverflow } from "./toolbar-overflow";

interface EmojiData {
  native: string;
}

export const EmojiToolbarButton = memo(function EmojiToolbarButton() {
  const editor = useEditorRef();
  const [open, setOpen] = useState(false);
  // The picker (Popover or DropdownMenu) steals focus, which clears
  // editor.selection. Track the most recent non-null selection so we can
  // restore it before insertText — without this, insertText is a no-op.
  const savedSelectionRef = useRef<typeof editor.selection>(null);
  useEditorSelector((editor) => {
    if (editor.selection) {
      savedSelectionRef.current = editor.selection;
    }
    return null;
  }, []);

  const handleSelect = useCallback(
    (emoji: EmojiData) => {
      const saved = editor.selection ?? savedSelectionRef.current;
      if (saved) {
        editor.tf.select(saved);
        editor.tf.focus();
        editor.tf.insertText(emoji.native);
      }
      setOpen(false);
    },
    [editor],
  );

  const { isOverflow } = useToolbarOverflow();

  if (isOverflow) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <Smile />
          Emoji
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="border-none p-0 shadow-none">
          <Picker
            data={data}
            onEmojiSelect={handleSelect}
            previewPosition="none"
            skinTonePosition="none"
            theme="light"
          />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <Popover modal={false} onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <div>
          <RedoButton
            dataTestId="emoji-toolbar-button"
            hierarchy="tertiary"
            IconLeading={Smile}
            size="sm"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto border-none p-0 shadow-none"
      >
        <Picker
          data={data}
          onEmojiSelect={handleSelect}
          previewPosition="none"
          skinTonePosition="none"
          theme="light"
        />
      </PopoverContent>
    </Popover>
  );
});
